import React from 'react';
import PropTypes from 'prop-types';

import { Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Media } from 'gatsby-plugin-fresnel';
import * as style from './ArticleHead.module.scss';
import { datePostedGmtToString } from '../../../utilities';
import parseBlocks from '../../../utilities/parseBlocks';

const ArticleHead = (props) => {
  const {
    sustainability = false,
    title,
    lead,
    imgData,
    imgAlt,
    imgHoverTitle,
    authorImgData,
    authorName,
    authorSlug,
    datePostedGmt,
    timeToRead,
    category,
    categoryLink,
    isDisableFeaturedImg,
    videoBlock,
    locale,
  } = props;

  return (
    <div className={style.articleHead}>
      <div className={style.titleWrapper}>
        <Media greaterThanOrEqual="tablet">
          {category && (
            <Link
              className={`clearLinkStyle d-block ${style.category}`}
              to={categoryLink}
            >
              {category}
            </Link>
          )}
        </Media>
        <h1 className={style.title}>{title}</h1>
        {lead && <p className={style.lead}>{lead}</p>}
        {(authorName || timeToRead || datePostedGmt) && (
          <div className={style.authorSection}>
            {!sustainability &&
              authorName &&
              authorImgData &&
              authorImgData?.images && (
                <Link className="clearLinkStyle d-block" to={authorSlug}>
                  <GatsbyImage
                    image={authorImgData}
                    className={style.authorPic}
                    width="40px"
                    height="40px"
                    alt={authorName}
                    loading="eager"
                    style={{ borderRadius: '50%' }}
                  />
                </Link>
              )}
            <div>
              {!sustainability &&
                authorName &&
                authorImgData &&
                authorImgData?.images && (
                  <Link
                    className={`${style.authorName} clearLinkStyle d-block`}
                    to={authorSlug}
                  >
                    {authorName}
                  </Link>
                )}
              {timeToRead && (
                <div className={style.timeToRead}>
                  <span>{datePostedGmtToString(datePostedGmt)}</span>
                  {!Number.isNaN(timeToRead) && (
                    <>
                      <span> · </span>
                      <span>{timeToRead} min read</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {!isDisableFeaturedImg && imgData && imgData?.images && (
        <GatsbyImage
          key="featuredImage"
          imgClassName=""
          image={imgData}
          alt={imgAlt}
          title={imgHoverTitle}
          loading="eager"
        />
      )}
      {isDisableFeaturedImg && parseBlocks([videoBlock], locale)}
    </div>
  );
};

ArticleHead.defaultProps = {
  authorImgData: '',
  authorName: '',
  authorSlug: '',
  datePostedGmt: '',
  imgAlt: 'Article featured image',
  imgData: null,
  imgHoverTitle: '',
  isDisableFeaturedImg: false,
  lead: '',
  timeToRead: null,
  videoBlock: null,
};

ArticleHead.propTypes = {
  /** Title of an Article. */
  title: PropTypes.string.isRequired,
  /** Lead - short post description. */
  lead: PropTypes.string,
  /** Featured image of an Article (gatsby-image data). Mobile version */
  imgData: PropTypes.any,
  /** Featured image alternative text. (also SEO) */
  imgAlt: PropTypes.string,
  /** Image title to display a tooltip on hover. */
  imgHoverTitle: PropTypes.string,
  /** Author's avatar image (gatsby-image data). */
  authorImgData: PropTypes.any,
  /** Author's name (first name + last name). */
  authorName: PropTypes.string,
  /** Author's slug - the link to their page. */
  authorSlug: PropTypes.string,
  /** Date when post has been published GMT (later converted to local time). */
  datePostedGmt: PropTypes.string,
  /** Approximate time to read (minutes). */
  timeToRead: PropTypes.number,
  /** Hide featured image to show video instead */
  isDisableFeaturedImg: PropTypes.bool,
  /** Featured video properties */
  videoBlock: PropTypes.object,
  /** Locale */
  locale: PropTypes.string.isRequired,
};

export default ArticleHead;
